import React, { useState, useEffect, useMemo } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, AreaChart, Area, Legend, LabelList } from 'recharts';
import SecureClientSelector from './SecureClientSelector';
import { useAuth } from '../App';
import { formatDateOrDash } from '../utils/dates';
import { getStatusLabel } from '../utils/status';
import { Filter, X } from 'lucide-react';
// rf-ui.css traz os tokens (:root) e as primitivas (.rf-card, .rf-table) que
// esta tela consome. Importado explicitamente para não depender de a casca
// tê-lo carregado antes.
import '../styles/rf-ui.css';
import '../styles/analytics-v3.css';
import { displayProtocolTag } from '../utils/protocol';

/* ------------------------------------------------------------------ *
 * Paleta de dados — v3
 *
 * Derivada da marca (design-system/tokens.css) e VALIDADA, não escolhida no
 * olho. Os valores saíram de scripts/validate_palette.js:
 *
 *  - categórica (ciano x laranja da marca): CVD ΔE 24.4, visão normal 32.7,
 *    ambos >= 3:1 sobre branco — todas as checagens passam;
 *  - rampa ordinal do status: L monotônica, ΔL >= 0.06 por passo, ponta clara
 *    2.32:1 sobre branco.
 *
 * Só a Evolução Temporal tem duas séries. Todo gráfico NOMINAL (tipos,
 * departamentos, unidades, público, conclusão) usa UMA cor: a barra já codifica
 * a grandeza, então colorir cada barra de um jeito gastaria o canal de
 * identidade para repetir o que o comprimento já diz.
 * ------------------------------------------------------------------ */

const S1 = '#009EDB';           // ciano da marca — série 1 / barras nominais
const S2 = '#EA5431';           // laranja da marca — série 2
const ORD = ['#3FB6E6', '#0E9AD3', '#0076A6', '#005274']; // rampa ordinal
const INK = '#0B2942';
const INK_MUTED = '#38495A';
const MUTED = '#7C8B9B';
const GRID = '#E8EAED';
const AXIS = '#C8CED6';

/** Ordem do fluxo — a rampa ordinal só faz sentido nesta sequência. */
const STATUS_ORDER = ['Recebida', 'Investigando', 'Resolvida', 'Fechada'];

const STATUS_BADGE = {
  Recebida: { bg: 'rgba(0,158,219,.12)', fg: '#00688F' },
  Investigando: { bg: 'rgba(234,84,49,.12)', fg: '#B23A1D' },
  Resolvida: { bg: 'rgba(12,163,12,.12)', fg: '#0A6B0A' },
  Fechada: { bg: 'rgba(11,41,66,.08)', fg: '#38495A' },
};

/** Tooltip no navy da marca. Enriquece a leitura — nunca é o único caminho
 *  para o valor: toda barra tem rótulo direto e a tabela abaixo repete tudo. */
const RfTooltip = ({ active, payload, label }) => {
  if (!active || !payload || !payload.length) return null;
  return (
    <div
      style={{
        background: '#031B31',
        borderRadius: 10,
        padding: '10px 12px',
        color: '#fff',
        fontSize: 12,
        boxShadow: '0 8px 24px rgba(3,27,49,.25)',
      }}
    >
      <p style={{ margin: '0 0 4px', fontWeight: 700 }}>
        {payload[0]?.payload?.fullName || label}
      </p>
      {payload.map((e, i) => (
        <p key={i} style={{ margin: '2px 0', color: 'rgba(255,255,255,.85)' }}>
          {e.name}: <strong style={{ color: '#fff' }}>{e.value}</strong>
        </p>
      ))}
    </div>
  );
};

/** Card padrão dos gráficos. */
const Card = ({ title, hint, span, children }) => (
  <section className={`rf-card${span ? ' rf-span-2' : ''}`}>
    <div className="rf-card__head">
      <h2 className="rf-card__title">{title}</h2>
      {hint && <span className="rf-card__hint">{hint}</span>}
    </div>
    <div className="rf-card__plot">{children}</div>
  </section>
);

/**
 * Barras horizontais ranqueadas.
 *
 * Horizontal e não vertical porque os rótulos são longos ("Recursos Humanos",
 * "Terceiro Comercial") — na vertical eles giravam 45° e ficavam ilegíveis.
 *
 * Altura em px calculada a partir do número de barras, e não em porcentagem:
 * porcentagem sobre um pai sem altura definida colapsa o gráfico, e uma altura
 * fixa cortaria a faixa do eixo, criando rolagem dentro do card.
 */
const RankedBars = ({ data, onSelect, colors, labelWidth = 116 }) => {
  const rows = data || [];
  const height = Math.max(140, rows.length * 34 + 16);
  return (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart data={rows} layout="vertical" margin={{ top: 0, right: 34, left: 0, bottom: 0 }}>
        {/* hairline sólida e recessiva — nunca tracejada */}
        <CartesianGrid stroke={GRID} horizontal={false} />
        <XAxis type="number" hide />
        <YAxis
          type="category"
          dataKey="name"
          width={labelWidth}
          tick={{ fill: INK_MUTED, fontSize: 11 }}
          axisLine={false}
          tickLine={false}
        />
        <Tooltip content={<RfTooltip />} cursor={{ fill: 'rgba(0,158,219,.06)' }} />
        {/* maxBarSize 18: marca fina, sobra ar na faixa. Ponta arredondada 4px,
            quadrada na linha de base. */}
        <Bar
          dataKey="value"
          radius={[0, 4, 4, 0]}
          maxBarSize={18}
          isAnimationActive={false}
          cursor={onSelect ? 'pointer' : 'default'}
          onClick={(entry) => onSelect && onSelect(entry?.fullName || entry?.name)}
        >
          {rows.map((row, i) => (
            <Cell key={i} fill={colors ? colors[i % colors.length] : S1} />
          ))}
          {/* Valor na ponta da barra: rótulo direto e seletivo, para o valor não
              depender só do tooltip. */}
          <LabelList
            dataKey="value"
            position="right"
            fill={INK}
            fontSize={11}
            fontWeight={700}
          />
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
};

const DisruptiveAnalytics = () => {
  const { user } = useAuth();
  const [realData, setRealData] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Estados para interatividade
  const [showDetails, setShowDetails] = useState(false);
  const [selectedDetail, setSelectedDetail] = useState(null);
  const [selectedClient, setSelectedClient] = useState('all'); // Novo filtro de cliente
  const [selectedFilters, setSelectedFilters] = useState({});

  const parseResponses = (responses) => {
    if (!responses) return {};
    if (typeof responses === 'object') return responses;
    try {
      return JSON.parse(responses);
    } catch (error) {
      console.warn('Não foi possível converter responses para objeto:', error);
      return {};
    }
  };

  // Conclusão (Grupo I) - desfecho da apuração da denúncia.
  // O banco mistura, nas colunas status_conclusao/conclusao, desfechos reais
  // (procedente, parcialmente procedente, improcedente, inconclusivo, fora do escopo)
  // com status de workflow e texto livre (Concluída, Em andamento, Teste, etc.).
  // Aqui reconhecemos APENAS os 5 desfechos canônicos (correspondência tolerante a
  // caixa/acento/variantes), checando ambas as colunas + admin_notes; tudo o que não
  // for um desfecho reconhecido (inclusive vazio/workflow/lixo) vira "Não informado".
  // Definidas no escopo do componente (não dentro de processRealData) para que o
  // filtro de clique (filteredData) use exatamente a mesma lógica dos gráficos.
  const matchConclusionOutcome = (value) => {
    if (value === null || value === undefined) return null;
    const n = String(value).trim().toLowerCase();
    if (!n) return null;
    if (n.includes('parcial') && n.includes('procedente')) return 'Parcialmente procedente';
    if (n.includes('improcedente')) return 'Improcedente';
    if (n.includes('procedente')) return 'Procedente';
    if (n.includes('inconclusiv')) return 'Inconclusivo';
    if (n.includes('fora do escopo')) return 'Fora do escopo';
    return null; // não é um desfecho reconhecido
  };
  const resolveConclusion = (complaint) => {
    const candidates = [complaint.status_conclusao, complaint.conclusao];
    if (complaint.admin_notes) {
      try {
        const notes = typeof complaint.admin_notes === 'string'
          ? (complaint.admin_notes.trim().startsWith('{') ? JSON.parse(complaint.admin_notes) : {})
          : complaint.admin_notes;
        candidates.push(notes.status_conclusao, notes.conclusao);
      } catch (e) { /* parse defensivo */ }
    }
    for (const candidate of candidates) {
      const outcome = matchConclusionOutcome(candidate);
      if (outcome) return outcome;
    }
    return 'Não informado';
  };

  // Função para processar dados reais em gráficos
  const processRealData = (complaints) => {
    if (!complaints || complaints.length === 0) return { 
      evolucao: [], tipos: [], departamentos: [], status: [], 
      anonimos: [], unidades: [], publico: [], conclusao: []
    };

    // Evolução temporal - últimos 12 meses
    const now = new Date();
    const months = [];
    for (let i = 11; i >= 0; i--) {
      const month = new Date(now.getFullYear(), now.getMonth() - i, 1);
      months.push({
        month: month.toLocaleDateString('pt-BR', { month: 'short', year: '2-digit' }),
        count: 0,
        resolved: 0,
        fullDate: month
      });
    }

    complaints.forEach(complaint => {
      const complaintDate = new Date(complaint.created_at);
      const monthKey = new Date(complaintDate.getFullYear(), complaintDate.getMonth(), 1);
      
      const monthIndex = months.findIndex(m => 
        m.fullDate.getTime() === monthKey.getTime()
      );
      
      if (monthIndex !== -1) {
        months[monthIndex].count++;
        if (complaint.status === 'resolved') {
          months[monthIndex].resolved++;
        }
      }
    });

    // Tipos de denúncia com legenda
    const tiposCount = {};
    complaints.forEach(complaint => {
      const tipo = complaint.responses?.tipo_denuncia || 'Outros';
      tiposCount[tipo] = (tiposCount[tipo] || 0) + 1;
    });
    const tipos = Object.entries(tiposCount)
      .sort(([,a], [,b]) => b - a)
      .slice(0, 15) // Aumentei para 15 para mostrar mais tipos
      .map(([name, value]) => ({
        name: name.length > 25 ? name.substring(0, 22) + '...' : name,
        fullName: name,
        value,
      }));

    // Departamentos (vertical agora)
    const deptCount = {};
    complaints.forEach(complaint => {
      const dept = complaint.responses?.departamento || 'Outros';
      deptCount[dept] = (deptCount[dept] || 0) + 1;
    });
    const departamentos = Object.entries(deptCount)
      .sort(([,a], [,b]) => b - a)
      .slice(0, 8)
      .map(([name, value]) => ({
        name: name.length > 15 ? name.substring(0, 12) + '...' : name,
        fullName: name,
        value,
      }));

    // Status
    const statusMap = {
      'received': { name: 'Recebida' },
      'investigating': { name: 'Investigando' },
      'resolved': { name: 'Resolvida' },
      'closed': { name: 'Fechada' }
    };

    const statusCount = {};
    complaints.forEach(complaint => {
      const status = complaint.status || 'received';
      const statusInfo = statusMap[status];
      if (statusInfo) {
        statusCount[statusInfo.name] = (statusCount[statusInfo.name] || 0) + 1;
      }
    });

    const status = Object.entries(statusCount).map(([name, value]) => ({ name, value }));

    // Anônimos vs Identificados
    const anonimosCount = { 'Anônimas': 0, 'Identificadas': 0 };
    complaints.forEach(complaint => {
      const identificacao = complaint.responses?.identificacao;
      // Normalizar para minúsculo para comparar (considera "Não", "não", "NAO", etc.)
      const identificacaoLower = identificacao?.toLowerCase();
      if (identificacaoLower === 'não' || identificacaoLower === 'nao') {
        anonimosCount['Anônimas']++;
      } else if (identificacaoLower === 'sim') {
        anonimosCount['Identificadas']++;
      } else {
        anonimosCount['Anônimas']++; // Default para anônimas se não especificado
      }
    });

    const anonimos = [
      { name: 'Anônimas', value: anonimosCount['Anônimas'] },
      { name: 'Identificadas', value: anonimosCount['Identificadas'] }
    ];

    // Unidades (local_ocorrencia)
    const unidadesCount = {};
    complaints.forEach(complaint => {
      const unidade = complaint.responses?.local_ocorrencia || 'Não informado';
      unidadesCount[unidade] = (unidadesCount[unidade] || 0) + 1;
    });
    const unidades = Object.entries(unidadesCount)
      .sort(([,a], [,b]) => b - a)
      .slice(0, 8)
      .map(([name, value]) => ({
        name: name.length > 15 ? name.substring(0, 12) + '...' : name,
        fullName: name,
        value,
      }));

    // Tipo de Público
    const publicoCount = {};
    complaints.forEach(complaint => {
      const publico = complaint.responses?.tipo_publico || 'Não informado';
      publicoCount[publico] = (publicoCount[publico] || 0) + 1;
    });
    const publico = Object.entries(publicoCount)
      .sort(([,a], [,b]) => b - a)
      .slice(0, 6)
      .map(([name, value]) => ({
        name: name.length > 20 ? name.substring(0, 17) + '...' : name,
        fullName: name,
        value,
      }));

    // Conclusão (Grupo I) - distribuição dos desfechos (resolveConclusion definida
    // no escopo do componente, compartilhada com o filtro de clique).
    const conclusaoCount = {};
    complaints.forEach(complaint => {
      const outcome = resolveConclusion(complaint);
      conclusaoCount[outcome] = (conclusaoCount[outcome] || 0) + 1;
    });
    // Ordem/cores fixas por desfecho para uma leitura consistente do gráfico.
    const conclusionOrder = ['Procedente', 'Parcialmente procedente', 'Improcedente', 'Inconclusivo', 'Fora do escopo', 'Não informado'];
    const conclusao = Object.entries(conclusaoCount)
      .sort(([a], [b]) => conclusionOrder.indexOf(a) - conclusionOrder.indexOf(b))
      .map(([name, value]) => ({ name, fullName: name, value }));

    return { 
      evolucao: months, 
      tipos, 
      departamentos, 
      status, 
      anonimos,
      unidades, 
      publico,
      conclusao
    };
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Construir URL com filtros
        const params = new URLSearchParams();
        if (selectedClient && selectedClient !== 'all') {
          params.append('client_slug', selectedClient);
        }
        
        const url = `https://reportfius-backend.up.railway.app/api/complaints/analytics/list${params.toString() ? '?' + params.toString() : ''}`;
        
        const response = await fetch(url, {
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json'
          }
        });

        if (response.ok) {
          const data = await response.json();
          const complaints = (data.complaints || []).map(complaint => ({
            ...complaint,
            responses: parseResponses(complaint.responses)
          }));
          console.log('✅ Dados REAIS carregados:', complaints.length);
          setRealData(complaints);
        } else {
          console.warn('Erro na API');
          setRealData([]);
        }
      } catch (error) {
        console.warn('Erro ao conectar API:', error);
        setRealData([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [selectedClient]);

  // Resetar filtros quando o cliente muda
  useEffect(() => {
    setSelectedFilters({});
    console.log('🔄 Cliente alterado, resetando filtros');
  }, [selectedClient]);

  // Processar dados para gráficos
  const filteredData = useMemo(() => {
    console.log('🔄 Recalculando filteredData:', {
      realDataLength: realData.length,
      selectedFilters,
      filtersCount: Object.keys(selectedFilters || {}).length
    });

    if (!selectedFilters || Object.keys(selectedFilters).length === 0) {
      console.log('✅ Sem filtros, retornando todos os dados');
      return realData;
    }

    const filtered = realData.filter(item => {
      return Object.entries(selectedFilters).every(([filterType, filterValue]) => {
        if (!filterValue) return true;

        if (filterType === 'departamento') {
          return (item.responses?.departamento || 'Outros') === filterValue;
        }
        if (filterType === 'tipo') {
          return (item.responses?.tipo_denuncia || 'Outros') === filterValue;
        }
        if (filterType === 'status') {
          const statusMap = { 
            'Recebida': 'received', 
            'Investigando': 'investigating', 
            'Resolvida': 'resolved', 
            'Fechada': 'closed' 
          };
          return item.status === statusMap[filterValue];
        }
        if (filterType === 'unidade') {
          return (item.responses?.local_ocorrencia || 'Não informado') === filterValue;
        }
        if (filterType === 'publico') {
          return (item.responses?.tipo_publico || 'Não informado') === filterValue;
        }
        if (filterType === 'conclusao') {
          return resolveConclusion(item) === filterValue;
        }
        if (filterType === 'anonimos') {
          const identificacao = item.responses?.identificacao;
          const identificacaoLower = identificacao?.toLowerCase();
          if (filterValue === 'Anônimas') {
            return identificacaoLower === 'não' || identificacaoLower === 'nao' || !identificacao;
          }
          return identificacaoLower === 'sim';
        }
        return true;
      });
    });

    console.log('✅ Filtragem concluída:', {
      original: realData.length,
      filtrado: filtered.length,
      filtros: selectedFilters
    });

    return filtered;
  }, [realData, selectedFilters]);

  // Processar dados para gráficos
  const chartData = useMemo(() => processRealData(filteredData), [filteredData]);

  // Debug: Verificar dados dos gráficos
  useEffect(() => {
    console.log('📊 chartData atualizado:', {
      tipos: chartData.tipos?.length || 0,
      anonimos: chartData.anonimos?.length || 0,
      departamentos: chartData.departamentos?.length || 0,
      status: chartData.status?.length || 0,
      unidades: chartData.unidades?.length || 0,
      publico: chartData.publico?.length || 0
    });
    console.log('📊 chartData.tipos:', chartData.tipos);
    console.log('📊 chartData.anonimos:', chartData.anonimos);
  }, [chartData]);

  // Função para filtrar dados
  const handleFilterClick = (filterType, filterValue) => {
    console.log('🔍 handleFilterClick chamado:', { filterType, filterValue });
    if (!filterValue) {
      console.warn('⚠️ filterValue está vazio');
      return;
    }

    setSelectedFilters(prev => {
      const currentValue = prev[filterType];
      const newFilters = { ...prev };

      if (currentValue === filterValue) {
        delete newFilters[filterType];
        console.log('✅ Removendo filtro:', filterType);
      } else {
        newFilters[filterType] = filterValue;
        console.log('✅ Aplicando filtro:', filterType, '=', filterValue);
      }

      console.log('📊 Filtros ativos:', newFilters);
      return newFilters;
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="bg-white rounded-xl shadow-lg p-8 text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <div className="text-gray-700 text-lg">
            🔄 Carregando Dashboard Analítico...
          </div>
        </div>
      </div>
    );
  }

  // Calcular KPIs (alinhados com a lógica dos gráficos)
  const totalComplaints = filteredData.length;
  // Resolvidas = status 'closed' (conforme gráfico de status mostra "Fechada")
  const resolvedComplaints = filteredData.filter(c => c.status === 'closed').length;
  // Investigando = status 'investigating' (conforme kanban)
  const investigatingComplaints = filteredData.filter(c => c.status === 'investigating').length;
  // Taxa de resolução = denúncias fechadas / total
  const resolutionRate = totalComplaints > 0 ? Math.round((resolvedComplaints / totalComplaints) * 100) : 0;
  // Anônimas = quando identificacao é 'Não' ou 'nao' (normalizado, conforme gráfico de identificação)
  const anonymousComplaints = filteredData.filter(c => {
    const identificacao = c.responses?.identificacao;
    const identificacaoLower = identificacao?.toLowerCase();
    return identificacaoLower === 'não' || identificacaoLower === 'nao';
  }).length;

  // ---- derivações só de apresentação (não tocam a camada de dados) ----
  const filtrosAtivos = Object.entries(selectedFilters || {});

  // Status na ordem do fluxo, para a rampa ordinal significar progressão.
  const statusOrdenado = STATUS_ORDER
    .map((nome) => (chartData.status || []).find((s) => s.name === nome))
    .filter(Boolean);

  // Identificação é uma proporção de DUAS classes: vira medidor, não gráfico de
  // barras com duas barras (nem pizza de duas fatias).
  const anonimas = (chartData.anonimos || []).find((d) => d.name === 'Anônimas')?.value || 0;
  const identificadas = (chartData.anonimos || []).find((d) => d.name === 'Identificadas')?.value || 0;
  const totalIdent = anonimas + identificadas;
  const pctAnonimas = totalIdent > 0 ? Math.round((anonimas / totalIdent) * 100) : 0;

  // Nominal ranqueado: 8 é o limite de leitura confortável numa coluna.
  const topTipos = (chartData.tipos || []).slice(0, 8);
  const tiposRestantes = Math.max((chartData.tipos || []).length - topTipos.length, 0);

  const kpis = [
    { label: 'Resolvidas', value: resolvedComplaints },
    { label: 'Investigando', value: investigatingComplaints },
    { label: 'Taxa de resolução', value: `${resolutionRate}%` },
    { label: 'Anônimas', value: anonymousComplaints },
  ];

  return (
    <div className="rf-an">
      {/* ---------------------------------------------------- cabeçalho */}
      <div className="rf-an__bar">
        <div>
          <h1 className="rf-an__title">Dashboard Analítico</h1>
          <p className="rf-an__subtitle">
            {filtrosAtivos.length > 0
              ? `${filteredData.length} de ${realData.length} denúncias no recorte atual`
              : `${realData.length} denúncias`}
          </p>
        </div>

        {/* Uma linha de filtros acima de tudo que ela afeta — nunca dentro do
            card de um gráfico. Todos os gráficos redesenham sobre o mesmo
            recorte. */}
        <div className="rf-an__filters">
          <Filter size={15} color={MUTED} aria-hidden="true" />
          <span style={{ fontSize: '.82rem', fontWeight: 700, color: INK_MUTED }}>Cliente</span>
          <SecureClientSelector
            selectedClient={selectedClient}
            onClientChange={(client) => setSelectedClient(client.client_slug)}
            className="w-56"
            showStats={false}
          />
          {filtrosAtivos.map(([tipo, valor]) => (
            <button
              key={`${tipo}-${valor}`}
              type="button"
              className="rf-an__chip"
              onClick={() => handleFilterClick(tipo, valor)}
              title="Remover este filtro"
            >
              {tipo}: {valor}
              <X size={13} aria-hidden="true" />
            </button>
          ))}
        </div>
      </div>

      {/* -------------------------------------------------------- KPIs */}
      <div className="rf-an__kpis">
        {/* Figura-herói: o número que o painel lidera. Uma só por tela. */}
        <div className="rf-kpi rf-kpi--hero">
          <p className="rf-kpi__label">Total de denúncias</p>
          <div className="rf-kpi__value">{totalComplaints}</div>
        </div>
        {kpis.map((k) => (
          <div key={k.label} className="rf-kpi">
            <p className="rf-kpi__label">{k.label}</p>
            <div className="rf-kpi__value">{k.value}</div>
          </div>
        ))}
      </div>

      {/* ------------------------------------------------------- grade */}
      <div className="rf-an__grid">
        {/* Série temporal: duas séries, então legenda obrigatória. Área a ~10%
            de opacidade (lavagem, nunca bloco saturado) e linha de 2px. */}
        <Card title="Evolução temporal" hint="últimos 12 meses" span>
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={chartData.evolucao} margin={{ top: 8, right: 12, left: -18, bottom: 0 }}>
              <defs>
                <linearGradient id="rfA1" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={S1} stopOpacity={0.18} />
                  <stop offset="100%" stopColor={S1} stopOpacity={0.02} />
                </linearGradient>
                <linearGradient id="rfA2" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={S2} stopOpacity={0.16} />
                  <stop offset="100%" stopColor={S2} stopOpacity={0.02} />
                </linearGradient>
              </defs>
              <CartesianGrid stroke={GRID} vertical={false} />
              <XAxis
                dataKey="month"
                tick={{ fill: MUTED, fontSize: 11 }}
                axisLine={{ stroke: AXIS }}
                tickLine={false}
                dy={4}
              />
              <YAxis
                tick={{ fill: MUTED, fontSize: 11 }}
                axisLine={false}
                tickLine={false}
                width={44}
                allowDecimals={false}
              />
              <Tooltip content={<RfTooltip />} />
              <Legend
                verticalAlign="top"
                align="right"
                height={28}
                iconType="plainline"
                iconSize={14}
                wrapperStyle={{ fontSize: 12, color: INK_MUTED }}
              />
              <Area
                type="monotone"
                dataKey="count"
                name="Recebidas"
                stroke={S1}
                strokeWidth={2}
                fill="url(#rfA1)"
                dot={false}
                activeDot={{ r: 4, strokeWidth: 2, stroke: '#fff' }}
                isAnimationActive={false}
              />
              <Area
                type="monotone"
                dataKey="resolved"
                name="Resolvidas"
                stroke={S2}
                strokeWidth={2}
                fill="url(#rfA2)"
                dot={false}
                activeDot={{ r: 4, strokeWidth: 2, stroke: '#fff' }}
                isAnimationActive={false}
              />
            </AreaChart>
          </ResponsiveContainer>
        </Card>

        {/* Status é ORDINAL (etapas de um fluxo): um matiz só, claro -> escuro,
            para a ordem ser lida na própria cor. */}
        <Card title="Status" hint="etapas do fluxo">
          <RankedBars
            data={statusOrdenado}
            colors={ORD}
            labelWidth={92}
            onSelect={(v) => handleFilterClick('status', v)}
          />
        </Card>

        <Card
          title="Tipos de denúncia"
          hint={tiposRestantes > 0 ? `top 8 de ${topTipos.length + tiposRestantes}` : undefined}
        >
          <RankedBars data={topTipos} onSelect={(v) => handleFilterClick('tipo', v)} />
        </Card>

        <Card title="Conclusão" hint="desfecho da apuração">
          <RankedBars
            data={chartData.conclusao}
            labelWidth={140}
            onSelect={(v) => handleFilterClick('conclusao', v)}
          />
        </Card>

        {/* Duas classes = proporção. Medidor, não gráfico. A trilha é um passo
            mais claro da MESMA rampa, para o estado ser lido na barra inteira. */}
        <Card title="Identificação" hint="anônimas x identificadas">
          <div className="rf-meter__row">
            <span className="rf-meter__value">{pctAnonimas}%</span>
            <span className="rf-meter__caption">
              {anonimas} de {totalIdent} anônimas
            </span>
          </div>
          <div className="rf-meter__track">
            <div className="rf-meter__fill" style={{ width: `${pctAnonimas}%` }} />
          </div>
          <div className="rf-meter__legend">
            <button
              type="button"
              className="rf-meter__key"
              style={{ background: 'none', border: 0, padding: 0, cursor: 'pointer', color: 'inherit' }}
              onClick={() => handleFilterClick('anonimos', 'Anônimas')}
            >
              <span className="rf-meter__dot" style={{ background: S1 }} />
              Anônimas <strong style={{ color: INK }}>{anonimas}</strong>
            </button>
            <button
              type="button"
              className="rf-meter__key"
              style={{ background: 'none', border: 0, padding: 0, cursor: 'pointer', color: 'inherit' }}
              onClick={() => handleFilterClick('anonimos', 'Identificadas')}
            >
              <span className="rf-meter__dot" style={{ background: 'rgba(0,158,219,.26)' }} />
              Identificadas <strong style={{ color: INK }}>{identificadas}</strong>
            </button>
          </div>
        </Card>

        <Card title="Departamentos" hint="mais reportados">
          <RankedBars
            data={chartData.departamentos}
            onSelect={(v) => handleFilterClick('departamento', v)}
          />
        </Card>

        <Card title="Unidades" hint="mais reportadas">
          <RankedBars data={chartData.unidades} onSelect={(v) => handleFilterClick('unidade', v)} />
        </Card>

        <Card title="Público" hint="origem do relato">
          <RankedBars
            data={chartData.publico}
            labelWidth={128}
            onSelect={(v) => handleFilterClick('publico', v)}
          />
        </Card>
      </div>

      {/* ------------------------------------------------------ tabela
          É também a "visão de tabela" que todo gráfico precisa ter: nenhum
          valor fica preso só no tooltip. */}
      <section style={{ marginTop: 12 }}>
        <div className="rf-card__head" style={{ padding: '4px 2px 10px' }}>
          <h2 className="rf-card__title">
            Denúncias {filtrosAtivos.length > 0 ? 'filtradas' : 'recentes'} ({filteredData.length})
          </h2>
          <span className="rf-card__hint">clique numa linha para ver o detalhe</span>
        </div>
        <div className="rf-table-wrap">
          <div style={{ maxHeight: 420, overflowY: 'auto' }}>
            <table className="rf-table">
              <thead>
                <tr>
                  {['ID', 'Data', 'Tipo', 'Departamento', 'Unidade', 'Público', 'Status', 'Relato'].map((h) => (
                    <th key={h}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredData.slice(0, 100).map((c) => {
                  const rotulo = getStatusLabel(c.status);
                  const badge = STATUS_BADGE[rotulo] || STATUS_BADGE.Fechada;
                  return (
                    <tr
                      key={c.id}
                      style={{ cursor: 'pointer' }}
                      onClick={() => {
                        setSelectedDetail({ type: 'complaint', data: c });
                        setShowDetails(true);
                      }}
                    >
                      <td className="rf-num">{displayProtocolTag(c)}</td>
                      <td>{formatDateOrDash(c.created_at)}</td>
                      <td>{c.responses?.tipo_denuncia || '—'}</td>
                      <td>{c.responses?.departamento || '—'}</td>
                      <td>{c.responses?.local_ocorrencia || '—'}</td>
                      <td>{c.responses?.tipo_publico || '—'}</td>
                      <td>
                        <span className="rf-badge" style={{ background: badge.bg, color: badge.fg }}>
                          {rotulo}
                        </span>
                      </td>
                      <td style={{ maxWidth: 260 }}>
                        <span
                          style={{
                            display: 'block',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {c.responses?.relato_detalhado || 'Sem relato disponível'}
                        </span>
                      </td>
                    </tr>
                  );
                })}
                {filteredData.length === 0 && (
                  <tr>
                    <td colSpan={8} style={{ textAlign: 'center', padding: 32, color: MUTED }}>
                      Nenhuma denúncia neste recorte.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
        {filteredData.length > 100 && (
          <p style={{ fontSize: '.75rem', color: MUTED, margin: '8px 2px 0' }}>
            Mostrando as 100 primeiras de {filteredData.length}.
          </p>
        )}
      </section>

      {/* ------------------------------------------------------- detalhe */}
      {showDetails && selectedDetail?.type === 'complaint' && (
        <div
          role="dialog"
          aria-modal="true"
          onClick={() => setShowDetails(false)}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(3,27,49,.55)',
            backdropFilter: 'blur(6px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: 20,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: '#fff',
              borderRadius: 20,
              maxWidth: 780,
              width: '100%',
              maxHeight: '82vh',
              overflow: 'auto',
              padding: 28,
              fontFamily: "'Mulish','Inter',system-ui,sans-serif",
            }}
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: 16,
                paddingBottom: 16,
                borderBottom: `1px solid ${GRID}`,
                marginBottom: 20,
              }}
            >
              <h3 style={{ fontSize: '1.3rem', fontWeight: 800, color: INK, margin: 0 }}>
                Denúncia {displayProtocolTag(selectedDetail.data)}
              </h3>
              <button
                type="button"
                onClick={() => setShowDetails(false)}
                aria-label="Fechar"
                style={{
                  background: 'none',
                  border: 0,
                  cursor: 'pointer',
                  color: MUTED,
                  display: 'flex',
                  padding: 6,
                }}
              >
                <X size={20} />
              </button>
            </div>

            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                gap: 18,
                marginBottom: 22,
              }}
            >
              {[
                ['Data', new Date(selectedDetail.data.created_at).toLocaleString('pt-BR')],
                ['Tipo', selectedDetail.data.responses?.tipo_denuncia || '—'],
                ['Departamento', selectedDetail.data.responses?.departamento || '—'],
                ['Unidade', selectedDetail.data.responses?.local_ocorrencia || '—'],
                ['Público', selectedDetail.data.responses?.tipo_publico || '—'],
                ['Status', getStatusLabel(selectedDetail.data.status)],
                [
                  'Identificação',
                  selectedDetail.data.responses?.identificacao === 'Sim' ? 'Identificada' : 'Anônima',
                ],
              ].map(([rotulo, valor]) => (
                <div key={rotulo}>
                  <p className="rf-kpi__label" style={{ marginBottom: 4 }}>
                    {rotulo}
                  </p>
                  <div style={{ fontWeight: 700, color: INK }}>{valor}</div>
                </div>
              ))}
            </div>

            <p className="rf-kpi__label" style={{ marginBottom: 6 }}>
              Relato
            </p>
            <div
              style={{
                padding: 16,
                background: '#F7F9FB',
                borderRadius: 12,
                lineHeight: 1.65,
                color: INK_MUTED,
                fontSize: '.92rem',
              }}
            >
              {selectedDetail.data.responses?.relato_detalhado || 'Sem relato disponível'}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DisruptiveAnalytics;
